import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import PageLayout from '../components/PageLayout.js';
import Reveal from '../components/Reveal.js';
import PageMeta from '../components/PageMeta.js';
import brand from '../brand';
import '../styles/_global.css';
import '../styles/home.css';

// Helper: Import all images from a directory (including subdirectories),
// already unwrapped to plain src strings.
function importAll(r) {
  return r.keys().map((key) => {
    const mod = r(key);
    return mod.default || mod;
  });
}

// Two separate photo pools:
// - `images`: the existing event-photo grid (lower res is fine, tiles are small)
// - `featuredImages`: a NEW folder for higher-res photos for the large banner.
//   Create `src/gallery_main/featured_photos/` (with at least one .webp in it)
//   before building — require.context resolves this at build time and will
//   fail if the folder doesn't exist yet.
const images = importAll(
  require.context('../gallery_main/event_photos/', true, /\.webp$/)
);
const featuredImages = importAll(
  require.context('../gallery_main/featured_photos/', true, /\.webp$/)
);

// Utility: unique random images from a pool, clamped so it never throws
function getUniqueRandomImages(pool, count = 9) {
  const safeCount = Math.min(count, pool.length);
  const shuffled = pool.slice().sort(() => Math.random() - 0.5);
  return shuffled.slice(0, safeCount);
}

// Utility: random image from a pool, excluding given values.
// Falls back to the event-photo pool if the given pool is empty (e.g. the
// featured folder hasn't been filled in yet), then to "no exclusions" if
// every remaining image is excluded.
function getRandomImageExcluding(pool, exclusions) {
  const usablePool = pool.length > 0 ? pool : images;
  const available = usablePool.filter((src) => !exclusions.includes(src));
  const finalPool = available.length > 0 ? available : usablePool;
  return finalPool[Math.floor(Math.random() * finalPool.length)];
}

// Preload an image before switching to it, so a crossfade never starts on a
// half-loaded image — matters most for the higher-res banner on mobile data.
function preloadImage(src) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => resolve(src);
    img.onerror = () => resolve(src);
    img.src = src;
  });
}

// GalleryItem: crossfades between images using AnimatePresence. Each new
// `src` mounts a new <motion.img>; the outgoing one fades out while the
// incoming one fades in, both handled by the library — no manual timers.
function GalleryItem({ src, alt, fadeDuration = 0.8 }) {
  return (
    <div className="gallery-item-container">
      <AnimatePresence mode="sync">
        <motion.img
          key={src}
          src={src}
          alt={alt}
          className="gallery-item"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: fadeDuration, ease: 'easeInOut' }}
        />
      </AnimatePresence>
    </div>
  );
}

// Owns its own rotation state/timer, isolated in its own component so it
// can never trigger a re-render (and re-init) of the particle background
// or anything else on the page.
function FeaturedPhoto() {
  const [featuredImage, setFeaturedImage] = useState(() =>
    getRandomImageExcluding(featuredImages, [])
  );

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      const next = getRandomImageExcluding(featuredImages, [featuredImage]);
      await preloadImage(next);
      if (!cancelled) setFeaturedImage(next);
    }, 5000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [featuredImage]);

  return (
    <Reveal as="section" className="featured-photo">
      <GalleryItem src={featuredImage} alt="Victorian Cruise Society event" fadeDuration={1.1} />
    </Reveal>
  );
}

// A single grid tile that owns its own rotation, on a randomized interval
// so tiles drift out of sync with each other instead of all ticking in
// lockstep — reads as an organic, living wall rather than a metronome.
function GalleryTile({ initialSrc }) {
  const [src, setSrc] = useState(initialSrc);

  useEffect(() => {
    let cancelled = false;
    // 6-12s, re-rolled every cycle, and different per tile — with 12 tiles
    // that averages roughly one photo changing every second across the
    // whole wall without any single tile feeling like it's on a timer.
    const delay = 6000 + Math.random() * 6000;
    const timer = setTimeout(async () => {
      const next = getRandomImageExcluding(images, [src]);
      await preloadImage(next);
      if (!cancelled) setSrc(next);
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [src]);

  return <GalleryItem src={src} alt="Victorian Cruise Society event photo" />;
}

// Picks the initial 12 once; each tile then rotates independently (see
// GalleryTile), so this component itself never needs to re-render.
function PhotoGallery() {
  const [initialImages] = useState(() => getUniqueRandomImages(images, 12));

  return (
    <Reveal as="section" className="gallery-grid-section" id="gallery">
      <h3>Life at VCS</h3>
      <div className="gallery-grid">
        {initialImages.map((src, index) => (
          <GalleryTile key={index} initialSrc={src} />
        ))}
      </div>
    </Reveal>
  );
}

const Home = () => {
  return (
    <PageLayout
      containerClassName="home-container"
      mainClassName="home-content"
      meta={
        <PageMeta
          description={`${brand.heroTitle} – ${brand.heroTagline}`}
          path="/"
        />
      }
    >
        <Reveal as="section" className="hero">
          <span className="hero-eyebrow">Est. 2024</span>
          <h1>{brand.heroTitle}</h1>
          <h2>{brand.heroTagline}</h2>
          <p className="hero-description">
            A private community of enthusiasts who share a passion for exceptional automobiles — from spirited cruises to weekend meets, we're driven by connection as much as horsepower.
          </p>
          <div className="hero-actions">
            <a href="/about" className="btn btn-primary">Meet the Team</a>
            <a href="#gallery" className="btn btn-outline">See the Gallery</a>
            <a href="/blog" className="btn btn-primary">Read Our Blog</a>
          </div>
        </Reveal>

        <FeaturedPhoto />

        <Reveal as="section" className="about-teaser">
          <h3>Who we are</h3>
          <p>
            Founded in 2024, we're the sister club of <strong>Three TRIPLE Zero</strong> and home to the <strong>GR Supra Club Victoria</strong> — built on trust, respect, and a shared love of the drive.
          </p>
          <a href="/about" className="btn btn-outline">Learn more about us</a>
        </Reveal>

        <PhotoGallery />
    </PageLayout>
  );
};

export default Home;